import type { ReactNode } from 'react';

export interface CalloutProps {
  readonly children: ReactNode;
  /**
   * `brand` informs; `info` carries a message that sits beside the screen's own copy; `warning` tells the
   * candidate why something behaves unusually; `error` says a state is not going to resolve by itself;
   * `success` confirms something that has just been done.
   *
   * The design system draws all of them as variants of one component set [`11875:19496` — `Tone=Brand`
   * `11875:19492`, `Tone=Warning` `11875:19493`, `Tone=Success` `11878:21237`, `Tone=Error` `11878:21240`,
   * `Tone=Info` `11909:25729`]. The warning tone arrived with KPC-1, where it explains that pasting is
   * disabled — a control that silently ignores a paste is a dead end, and the design answers it with words
   * rather than with a shrug. The error tone arrived with
   * KAD-6c's `05 · Locked`, and the distance between the two is the point there: a countdown is a retry and
   * a lock is not, so they may not read alike [AC-6]. The success tone arrived with KAD-6d's `09 · Ready`,
   * where it carries the line naming the kiosk and the venue it is now bound to [KAD-6d AC-5]. The info
   * tone arrived with KAP-5a's `12 · Your next steps`, where it carries decision 0360's own-phone sentence
   * under the welcome-email paragraph, a colour apart from the brand-toned timing note below it.
   */
  readonly tone?: 'brand' | 'info' | 'warning' | 'error' | 'success';
  /**
   * The optional control the frame draws BELOW the text, 8px under it [Figma · `Action link` inside
   * `11875:19492`].
   *
   * A slot rather than part of `children` because the frame gives the callout two stacked items with an
   * 8px gap, and `children` is one paragraph: folding a control into it would put the button inside the
   * `<p>` and lose the gap the design measures. Most callers pass nothing and render exactly as before —
   * on `06 · Jobs` the instance shows it, on `10 · Finish` the same component hides it.
   */
  readonly action?: ReactNode;
}

/**
 * The brand-toned information panel [Figma · component 11875:19492].
 *
 * On check-in it carries the four-factor rule. `--primary-muted` on `--primary-muted-foreground` measures
 * 7.4:1, so the tone is decorative rather than load-bearing — the text stands on its own.
 */
export function Callout({ children, tone = 'brand', action }: CalloutProps) {
  // All five pairs are measured, and all five clear 4.5:1 — the tone is decoration and the text stands on
  // its own, which is also why no state here is carried by colour alone.
  const COLOURS = {
    brand: 'bg-[var(--primary-muted)] text-[var(--primary-muted-foreground)]',
    // #00354b on #f6fcff measures 12.6:1.
    info: 'bg-[var(--info-muted)] text-[var(--info-muted-foreground)]',
    warning: 'bg-[var(--warning-muted)] text-[var(--warning-muted-foreground)]',
    error: 'bg-[var(--error-muted)] text-[var(--error-muted-foreground)]',
    // #183900 on #f4ffef measures 12.5:1.
    success: 'bg-[var(--success-callout)] text-[var(--success-callout-foreground)]',
  } as const;
  const colours = COLOURS[tone];

  return (
    <div
      className={`flex w-full flex-col items-start gap-2 rounded-[var(--radius-sm)] p-4 ${colours}`}
      /*
        The tone is carried in the DOM because it is the only thing distinguishing these two panels to a
        test: the colours are Tailwind arbitrary values, and asserting a class string proves nothing about
        which tone was chosen. An I-9 test claiming "the amber warning is gone" passed with the warning
        restored under the new copy, because nothing observed the tone.
      */
      data-tone={tone}
    >
      <p className="text-sm leading-5">{children}</p>
      {action}
    </div>
  );
}
